import { Fragment, memo, useEffect, useMemo, useState } from 'react'
import SimpleBarBox from '../SimpleBarBox'
import useStickySidebar from '../../hooks/useStickySidebar'
import { formatStake, formatBetAmount, formatRouletteBetName } from '../../utils/casinoUtils'
import CasinoViewMore, { formatPlaceDate } from './CasinoViewMore'

// Admin reference right sidebar — "My Bets" card (UserName/Rate/Amount of the
// downline bets on the current round) + mobile floating Bets counter + the
// "View More" button + modal (CasinoViewMore).
// outerSimpleBar: the reference makes the outer .right-sidebar a SimpleBar root
// only on lucky15, ballbyball, goal and goal2; everywhere else it is a plain div.
// Phones (< 768px) get no SimpleBar at all — the reference renders plain divs there.
const PHONE_MAX_WIDTH = 767
const isPhoneWidth = () => typeof window !== 'undefined' && window.innerWidth <= PHONE_MAX_WIDTH

// Andar Bahar 50 / 150 cards: the reference shows the deal position too
// ("Andar Q/7"). The settlement-facing `selection` stays "Andar Q", the full
// label is stored in player_name.
const POSITION_LABEL_GAMES = new Set(['ab3', 'ab4'])

const SIDEBAR_BET_LIMIT = 10

// Super Over / 5Five / sports pages use #my-game-bets; tables that use this sidebar use #casino-bets.
// Lands the card right under the fixed #page-topbar, so the "My Bets" heading is not hidden.

// Bottom edge of the fixed header on screen. Children can overflow #page-topbar (fixtures
// strip, search), so its own height is not enough — take the lowest visible child too.
function headerBottom() {
  const header = document.getElementById('page-topbar')
  if (!header) return 0
  let bottom = header.getBoundingClientRect().bottom
  for (const child of header.querySelectorAll('*')) {
    const r = child.getBoundingClientRect()
    if (r.height > 0 && r.bottom > bottom && r.bottom < window.innerHeight / 2) bottom = r.bottom
  }
  return Math.max(0, bottom)
}

export function scrollToMyBets() {
  const el = document.getElementById('my-game-bets') || document.getElementById('casino-bets')
  if (!el) return
  const targetTop = () => Math.max(0, el.getBoundingClientRect().top + window.scrollY - headerBottom())
  window.scrollTo({ top: targetTop(), behavior: 'smooth' })
  // The page can shift during the smooth scroll (live odds / cards re-render), which leaves
  // the heading under the header — re-check once it has stopped and snap to the exact spot.
  let done = false
  const fix = () => {
    if (done) return
    done = true
    window.removeEventListener('scrollend', fix)
    if (Math.abs(window.scrollY - targetTop()) > 2) window.scrollTo({ top: targetTop(), behavior: 'instant' })
  }
  window.addEventListener('scrollend', fix)
  setTimeout(fix, 1000) // browsers without scrollend
}

const CasinoMyBetsSidebar = memo(function CasinoMyBetsSidebar({ myBets = [], gameName = '', extra = null, outerSimpleBar = false, innerRightSidebar = true, formatNation = (n) => n, formatOdds = (bet) => formatStake(bet.odds ?? bet.rate) }) {
  const nationOf = (bet) => {
    if (POSITION_LABEL_GAMES.has(gameName) && bet.player_name) return bet.player_name
    const name = bet.selection || bet.player_name || ''
    // Unique Roulette: "19 to 36 - Half", "10 - Single" like the reference.
    return formatRouletteBetName(gameName, name, bet)
  }
  const [isPhone, setIsPhone] = useState(isPhoneWidth)
  useEffect(() => {
    const onResize = () => setIsPhone(isPhoneWidth())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  // Reference: the My Bets card lists only the LATEST 10 bets (View More has the
  // rest). The 10 newest by placement time, kept in the page's own order (Super
  // Over groups by market). Display only — the page's book still uses every bet.
  const shownBets = useMemo(() => {
    if (myBets.length <= SIDEBAR_BET_LIMIT) return myBets
    const time = (b) => new Date(b.created_at || b.createdAt || 0).getTime()
    const latest = new Set([...myBets].sort((a, b) => time(b) - time(a)).slice(0, SIDEBAR_BET_LIMIT))
    return myBets.filter((b) => latest.has(b))
  }, [myBets])
  // `sticky` only after the page scrolls past 12px at width >= 1280 (reference);
  // never on phones, where it would pin My Bets over the table.
  const stickyClass = useStickySidebar()
  const useOuterBar = outerSimpleBar && !isPhone
  // Plain render helpers (not components), so the card is not remounted on every render.
  const wrapOuter = (className, children) => (useOuterBar
    ? <SimpleBarBox className={className} maxHeight={null}>{children}</SimpleBarBox>
    : <div className={className}>{children}</div>)
  // Reference: the Bets counter sits inside the inner .right-sidebar; premium
  // baccarat has no inner .right-sidebar at all.
  const wrapInnerSidebar = (children) => (innerRightSidebar
    ? <div className="right-sidebar">{children}</div>
    : children)
  const wrapInner = (children) => (isPhone
    ? children
    : <SimpleBarBox maxHeight={null} autoHide>{children}</SimpleBarBox>)

  // Mobile "Bets" counter click → My Bets card pe smooth scroll
  const scrollToBets = () => {
    scrollToMyBets()
  }

  const isLayBet = (bet) => String(bet.game_type || bet.type || '').toLowerCase() === 'lay'

  return (
    wrapOuter(`right-sidebar${stickyClass}`, <>
      {wrapInnerSidebar(<>
      {/* Reference (desktop): [outer SimpleBar on 4 tables] > right-sidebar > auto-hide SimpleBar > card. */}
      {wrapInner(
      <div className="casino-right-sidebar">
                    <div id="casino-bets" className="card m-b-10 my-bet">
                      <div className="card-header">
                        <h6 className="card-title float-left">My Bets</h6>
                        {' '}
                        <CasinoViewMore gameName={gameName} nationOf={(bet) => formatNation(nationOf(bet), bet)} formatOdds={formatOdds} />
                      </div>
                      {' '}
                      <div className="card-body1">
                        <div className="tab-content">
                          <div id="matched-bet" className="tab-pane active">
                            <div className="table-responsive1">
                              <table className="table coupon-table table-striped mb-0">
                                <thead>
                                  <tr>
                                    <th style={{ minWidth: '90px' }}>UserName</th>
                                    <th className="text-right" style={{ minWidth: '50px' }}>Rate</th>
                                    <th className="text-right" style={{ minWidth: '70px' }}>Amount</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {shownBets.length === 0 ? (
                                    <tr>
                                      <td colSpan="4" className="text-center">No records found</td>
                                    </tr>
                                  ) : (
                                    shownBets.map((bet, i) => {
                                      const border = isLayBet(bet) ? 'lay-border' : 'back-border'
                                      return (
                                        <Fragment key={bet.id || bet._id || i}>
                                          <tr className={border}>
                                            <td colSpan="4">
                                              <b>{formatNation(nationOf(bet), bet)}</b>
                                              {' '}
                                              <span className="float-right">{formatPlaceDate(bet.created_at || bet.createdAt)}</span>
                                            </td>
                                          </tr>
                                          <tr className={border}>
                                            <td>{bet.username || bet.user_name || bet.user_id || ''}</td>
                                            <td className="text-right">{formatOdds(bet)}</td>
                                            <td className="text-right">{formatBetAmount(bet.amount ?? bet.stake)}</td>
                                          </tr>
                                          <tr>
                                            <td colSpan="4" style={{ height: '3px', padding: '0px' }}></td>
                                          </tr>
                                        </Fragment>
                                      )
                                    })
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
      {extra}
      </div>
      )}
      {' '}
      <div className="market-show-icon d-none-desktop" onClick={scrollToBets}>
        {/* Counts what the card lists (latest 10), not every bet in the round */}
        <div className="bet-cnt">{Math.min(shownBets.length, 10)}</div>
        {' '}
        <div className="bet-title">Bets</div>
      </div>
      </>)}
      {' '}
    </>)
  )
})

export default CasinoMyBetsSidebar
